import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useEffect, type RefObject } from 'react'
import { type CalendarDay } from '../monthGrid'
import { readDragData } from './dragData'
import { buildDropProposal, type CalendarDropProposal } from './dropProposal'
import { useCalendarDragState } from './useCalendarDragState'

/**
 * Makes one day cell somewhere a dragged item can land.
 *
 * The proposal is worked out as the pointer arrives so the cell can say whether
 * it will take the item, and worked out again on the drop rather than kept from
 * the hover — the same cell can be entered and left several times before a
 * person lets go.
 */
export const useDayDropTarget = ({
  day,
  isEnabled,
  onDrop,
  ref,
}: {
  day: CalendarDay
  isEnabled: boolean
  onDrop?: (proposal: CalendarDropProposal) => void
  ref: RefObject<HTMLElement>
}) => {
  const onDragEnter = useCalendarDragState((state) => state.onDragEnter)
  const onDragLeave = useCalendarDragState((state) => state.onDragLeave)
  const reset = useCalendarDragState((state) => state.reset)

  useEffect(() => {
    const element = ref.current
    if (!element || !isEnabled) return

    const propose = (
      data: Record<string | symbol, unknown>
    ): CalendarDropProposal | null => {
      const dragged = readDragData(data)
      if (dragged === null) return null
      return buildDropProposal({ ...dragged, targetDate: day.date })
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => readDragData(source.data) !== null,
      onDragEnter: ({ source }) =>
        onDragEnter(day.key, propose(source.data)?.forbiddenReason ?? null),
      onDragLeave: () => onDragLeave(day.key),
      onDrop: ({ source }) => {
        reset()
        const proposal = propose(source.data)
        if (proposal === null || proposal.forbiddenReason !== null) return
        onDrop?.(proposal)
      },
    })
  }, [day, isEnabled, onDragEnter, onDragLeave, onDrop, ref, reset])
}
